import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';

export interface DashboardSchwabOrphan {
  accountId: string;
  accountNickname?: string;
  symbol: string;
  longQuantity: number;
  shortQuantity: number;
  marketValue: number;
  isShort: boolean;
}

interface SchwabOrphanCardProps {
  orphan: DashboardSchwabOrphan;
  closing?: boolean;
  onClose: (orphan: DashboardSchwabOrphan) => void;
}

const formatCurrency = (value: number) => {
  if (Number.isNaN(value)) return '$0.00';
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const SchwabOrphanCard: React.FC<SchwabOrphanCardProps> = ({
  orphan,
  closing = false,
  onClose,
}) => {
  const qty = orphan.isShort ? orphan.shortQuantity : orphan.longQuantity;

  return (
    <div
      className={`rounded-lg border-2 p-4 shadow-sm ${
        orphan.isShort
          ? 'border-red-500 bg-red-50'
          : 'border-amber-500 bg-amber-50'
      }`}
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <div className="mb-1 flex items-center gap-2">
            <AlertTriangle
              className={`h-4 w-4 ${
                orphan.isShort ? 'text-red-600' : 'text-amber-600'
              }`}
            />
            <span
              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${
                orphan.isShort
                  ? 'bg-red-600 text-white'
                  : 'bg-amber-600 text-white'
              }`}
            >
              Schwab orphan
            </span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">{orphan.symbol}</h3>
          <p className="text-xs text-gray-600">
            {orphan.accountNickname || orphan.accountId}
          </p>
        </div>
        <span
          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
            orphan.isShort
              ? 'bg-red-100 text-red-800'
              : 'bg-amber-100 text-amber-900'
          }`}
        >
          {orphan.isShort ? 'SHORT' : 'LONG'}
        </span>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3">
        <div>
          <div className="text-sm text-gray-500">Quantity</div>
          <div className="text-lg font-semibold text-gray-900">{qty}</div>
        </div>
        <div>
          <div className="text-sm text-gray-500">Market value</div>
          <div className="text-lg font-semibold text-gray-900">
            {formatCurrency(orphan.marketValue)}
          </div>
        </div>
      </div>

      <p className="mb-4 text-sm text-gray-700">
        On Schwab but not tracked by Marlin. Close with a MARKET flatten on the
        broker.
      </p>

      <div className="flex items-center justify-between gap-2">
        <Link
          to={`/app/accounts/${orphan.accountId}`}
          className="text-sm font-medium text-primary-700 hover:text-primary-800"
        >
          Account detail
        </Link>
        <button
          type="button"
          onClick={() => onClose(orphan)}
          disabled={closing}
          className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-medium text-white disabled:opacity-50 ${
            orphan.isShort
              ? 'bg-red-600 hover:bg-red-700'
              : 'bg-amber-600 hover:bg-amber-700'
          }`}
        >
          <X className="mr-1.5 h-4 w-4" />
          {closing ? 'Closing…' : 'Close orphan'}
        </button>
      </div>
    </div>
  );
};

export default SchwabOrphanCard;
